/**
 * Chunk-load error recovery.
 *
 * After a deployment, a long-lived browser tab still references the previous
 * build's hashed chunk filenames. The next client-side navigation asks webpack
 * to load a chunk the server no longer serves, producing a `ChunkLoadError`.
 * This helper detects that specific failure and performs a single, guarded
 * full-page reload so the tab silently moves onto the current build — turning a
 * dead-end error screen into a seamless refresh.
 *
 * A short cooldown guards against reload loops: if a chunk error recurs within
 * `RELOAD_COOLDOWN_MS` of a prior auto-reload, we stop and let the error
 * boundary render its manual recovery UI instead.
 */

// sessionStorage key holding the timestamp (ms) of the last auto-reload attempt.
const RELOAD_ATTEMPT_KEY = 'fieldone:chunk-reload-attempt';

// Minimum time between auto-reloads. A recurrence inside this window is treated
// as "the reload did not help", so we surface the error instead of looping.
const RELOAD_COOLDOWN_MS = 10_000;

// Signatures webpack (and native ESM) use for dynamic-import/chunk failures.
const CHUNK_ERROR_PATTERN =
  /Loading (?:CSS )?chunk [\w-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

/**
 * Returns true when the provided value looks like a webpack dynamic-import /
 * chunk-loading failure (as opposed to an ordinary application error).
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'string') return CHUNK_ERROR_PATTERN.test(error);

  if ((error as { name?: unknown }).name === 'ChunkLoadError') return true;

  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && CHUNK_ERROR_PATTERN.test(message);
};

/**
 * If `error` is a chunk-load failure, perform a single guarded full-page reload
 * to recover onto the current build.
 *
 * No-ops for non-chunk errors, on the server, or when a recent reload already
 * occurred (loop guard).
 *
 * @returns `true` when a reload was triggered, otherwise `false`.
 */
export const maybeReloadOnChunkError = (error: unknown): boolean => {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(error)) return false;

  try {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)) || 0;

    // A reload already happened very recently and clearly did not resolve the
    // issue — stop so the error boundary can show its manual recovery UI.
    if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) return false;

    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, Date.now().toString());
  } catch {
    // sessionStorage may be unavailable (private mode/quota). Proceed with a
    // best-effort single reload rather than blocking recovery.
  }

  window.location.reload();
  return true;
};
